import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Save, Image, Type, QrCode, PenTool, Move, Eye, Upload, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';

const ELEMENT_TYPES = {
  STUDENT_NAME: { id: 'student_name', label: 'Nombre Estudiante', color: '#2563eb', icon: Type },
  COURSE_NAME: { id: 'course_name', label: 'Nombre Curso', color: '#16a34a', icon: Type },
  DATE: { id: 'date', label: 'Fecha', color: '#9333ea', icon: Type },
  CODE: { id: 'code', label: 'Código', color: '#ea580c', icon: Type },
  SIGNATURE: { id: 'signature', label: 'Firma Digital', color: '#0891b2', icon: PenTool },
  QR_CODE: { id: 'qr', label: 'Código QR', color: '#64748b', icon: QrCode },
};

export const CertificateTemplateEditor = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { templateId } = useParams();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [template, setTemplate] = useState(null);
  const [templateImage, setTemplateImage] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageScale, setImageScale] = useState(1);
  const [courses, setCourses] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const fileInputRef = useRef(null);
  
  // Dragging state
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    image_url: '',
    course_id: '',
    student_name_x: 400,
    student_name_y: 350,
    student_name_size: 48,
    course_name_x: 400,
    course_name_y: 450,
    course_name_size: 36,
    date_x: 400,
    date_y: 550,
    date_size: 24,
    code_x: 400,
    code_y: 650,
    code_size: 20,
    signature_url: '',
    signature_x: 600,
    signature_y: 700,
    signature_width: 200,
    signature_height: 100,
    qr_x: 100,
    qr_y: 700,
    qr_size: 150,
  });

  useEffect(() => {
    if (user && !['superadmin', 'admin'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate, templateId]);

  const loadData = async () => {
    try {
      const [coursesRes, mediaRes] = await Promise.all([
        api.get('/courses/'),
        api.get('/media/')
      ]);
      setCourses(coursesRes.data);
      setMediaFiles(mediaRes.data);

      if (templateId && templateId !== 'new') {
        const res = await api.get('/certificate-templates/');
        const found = res.data.find(t => t.id === templateId);
        if (found) {
          setTemplate(found);
          setFormData({
            name: found.name || '',
            image_url: found.image_url || '',
            course_id: found.course_id || '',
            student_name_x: found.student_name_x || 400,
            student_name_y: found.student_name_y || 350,
            student_name_size: found.student_name_size || 48,
            course_name_x: found.course_name_x || 400,
            course_name_y: found.course_name_y || 450,
            course_name_size: found.course_name_size || 36,
            date_x: found.date_x || 400,
            date_y: found.date_y || 550,
            date_size: found.date_size || 24,
            code_x: found.code_x || 400,
            code_y: found.code_y || 650,
            code_size: found.code_size || 20,
            signature_url: found.signature_url || '',
            signature_x: found.signature_x || 600,
            signature_y: found.signature_y || 700,
            signature_width: found.signature_width || 200,
            signature_height: found.signature_height || 100,
            qr_x: found.qr_x || 100,
            qr_y: found.qr_y || 700,
            qr_size: found.qr_size || 150,
          });
        }
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Load template image when URL changes
  useEffect(() => {
    if (formData.image_url) {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      const imageUrl = formData.image_url.startsWith('http') 
        ? formData.image_url 
        : `${process.env.REACT_APP_BACKEND_URL}${formData.image_url}`;
      
      img.onload = () => {
        setTemplateImage(img);
        setImageLoaded(true);
        
        // Calculate scale to fit container
        if (containerRef.current) {
          const containerWidth = containerRef.current.clientWidth - 40;
          const scale = Math.min(1, containerWidth / img.width);
          setImageScale(scale);
        }
      };
      img.onerror = () => {
        setImageLoaded(false);
        toast.error('Error al cargar la imagen de plantilla');
      };
      img.src = imageUrl;
    } else {
      setImageLoaded(false);
      setTemplateImage(null);
    }
  }, [formData.image_url]);

  // Get element position for rendering
  const getElementPosition = useCallback((elementId) => {
    switch (elementId) {
      case 'student_name':
        return { x: formData.student_name_x, y: formData.student_name_y, size: formData.student_name_size };
      case 'course_name':
        return { x: formData.course_name_x, y: formData.course_name_y, size: formData.course_name_size };
      case 'date':
        return { x: formData.date_x, y: formData.date_y, size: formData.date_size };
      case 'code':
        return { x: formData.code_x, y: formData.code_y, size: formData.code_size };
      case 'signature':
        return { x: formData.signature_x, y: formData.signature_y, width: formData.signature_width, height: formData.signature_height };
      case 'qr':
        return { x: formData.qr_x, y: formData.qr_y, size: formData.qr_size };
      default:
        return { x: 0, y: 0, size: 24 };
    }
  }, [formData]);

  // Update element position
  const updateElementPosition = useCallback((elementId, x, y) => {
    const updates = {};
    switch (elementId) {
      case 'student_name':
        updates.student_name_x = Math.round(x);
        updates.student_name_y = Math.round(y);
        break;
      case 'course_name':
        updates.course_name_x = Math.round(x);
        updates.course_name_y = Math.round(y);
        break;
      case 'date':
        updates.date_x = Math.round(x);
        updates.date_y = Math.round(y);
        break;
      case 'code':
        updates.code_x = Math.round(x);
        updates.code_y = Math.round(y);
        break;
      case 'signature':
        updates.signature_x = Math.round(x);
        updates.signature_y = Math.round(y);
        break;
      case 'qr':
        updates.qr_x = Math.round(x);
        updates.qr_y = Math.round(y);
        break;
      default:
        break;
    }
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  // Mouse handlers for drag and drop
  const handleMouseDown = (e, elementId) => {
    e.preventDefault();
    const pos = getElementPosition(elementId);
    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left) / imageScale;
    const mouseY = (e.clientY - rect.top) / imageScale;
    
    setDragging(elementId);
    setDragOffset({
      x: mouseX - pos.x,
      y: mouseY - pos.y
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!dragging || !containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left) / imageScale;
    const mouseY = (e.clientY - rect.top) / imageScale;
    
    const newX = mouseX - dragOffset.x;
    const newY = mouseY - dragOffset.y;
    
    // Clamp to image bounds
    const maxX = templateImage ? templateImage.width : 1920;
    const maxY = templateImage ? templateImage.height : 1080;
    
    updateElementPosition(
      dragging,
      Math.max(0, Math.min(newX, maxX)),
      Math.max(0, Math.min(newY, maxY))
    );
  }, [dragging, dragOffset, imageScale, templateImage, updateElementPosition]);

  const handleMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, handleMouseMove, handleMouseUp]);

  const handleDirectImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Solo se permiten imágenes JPG, PNG o WebP');
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const res = await api.post('/media/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploadedUrl = res.data.url;
      setFormData(prev => ({ ...prev, image_url: uploadedUrl }));
      // Refresh media list
      const mediaRes = await api.get('/media/');
      setMediaFiles(mediaRes.data);
      toast.success('Imagen subida y aplicada como fondo');
    } catch (err) {
      toast.error('Error al subir la imagen');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.image_url) {
      toast.error('Nombre e imagen son obligatorios');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        image_url: formData.image_url,
        course_id: formData.course_id || null,
        student_name_x: formData.student_name_x,
        student_name_y: formData.student_name_y,
        student_name_size: formData.student_name_size,
        course_name_x: formData.course_name_x,
        course_name_y: formData.course_name_y,
        course_name_size: formData.course_name_size,
        date_x: formData.date_x,
        date_y: formData.date_y,
        date_size: formData.date_size,
        code_x: formData.code_x,
        code_y: formData.code_y,
        code_size: formData.code_size,
        signature_url: formData.signature_url || null,
        signature_x: formData.signature_x,
        signature_y: formData.signature_y,
        signature_width: formData.signature_width,
        signature_height: formData.signature_height,
        qr_x: formData.qr_x,
        qr_y: formData.qr_y,
        qr_size: formData.qr_size,
      };

      if (templateId && templateId !== 'new') {
        await api.put(`/certificate-templates/${templateId}/`, payload);
        toast.success('Plantilla actualizada');
      } else {
        await api.post('/certificate-templates/', payload);
        toast.success('Plantilla creada');
      }
      navigate('/admin/certificate-templates');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Render draggable element
  const renderDraggableElement = (elementType) => {
    const pos = getElementPosition(elementType.id);
    const isSignature = elementType.id === 'signature';
    const isQR = elementType.id === 'qr';
    const isCode = elementType.id === 'code';
    const Icon = elementType.icon;
    
    const width = isSignature ? formData.signature_width : (isQR ? formData.qr_size : Math.max(100, pos.size * 6));
    // Code element taller to represent 2 lines
    const height = isSignature ? formData.signature_height : (isQR ? formData.qr_size : (isCode ? pos.size * 3 : pos.size * 1.5));

    return (
      <div
        key={elementType.id}
        data-testid={`draggable-${elementType.id}`}
        className={`absolute cursor-move flex flex-col items-center justify-center rounded border-2 border-dashed transition-shadow ${dragging === elementType.id ? 'shadow-lg ring-2 ring-offset-1' : 'hover:shadow-md'}`}
        style={{
          left: pos.x * imageScale,
          top: pos.y * imageScale,
          width: width * imageScale,
          height: height * imageScale,
          backgroundColor: `${elementType.color}20`,
          borderColor: elementType.color,
          ringColor: elementType.color,
        }}
        onMouseDown={(e) => handleMouseDown(e, elementType.id)}
      >
        <div 
          className="flex items-center gap-1 px-2 py-1 rounded text-white text-xs font-medium whitespace-nowrap"
          style={{ backgroundColor: elementType.color }}
        >
          <Icon className="w-3 h-3" />
          <span>{elementType.label}</span>
          <Move className="w-3 h-3 ml-1" />
        </div>
        {isCode && (
          <div className="text-xs mt-1 text-center leading-tight" style={{ color: elementType.color, fontSize: '9px' }}>
            Verificar:<br/>IAASP-XXXXXXXX
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div className="p-8">Cargando...</div>;

  return (
    <div className="min-h-screen bg-secondary" data-testid="certificate-template-editor">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/certificate-templates')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <h1 className="text-xl font-bold text-primary">
              {templateId === 'new' ? 'Nueva Plantilla' : 'Editar Plantilla'}
            </h1>
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Settings */}
          <div className="lg:col-span-1 space-y-4">
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre de la Plantilla *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ej: Certificado Principal"
                    data-testid="template-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Curso Específico (opcional)</Label>
                  <Select 
                    value={formData.course_id || 'global'} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, course_id: v === 'global' ? '' : v }))}
                  >
                    <SelectTrigger data-testid="course-select">
                      <SelectValue placeholder="Global (todos los cursos)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="global">Global (todos los cursos)</SelectItem>
                      {courses.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Imagen de Fondo *</Label>
                  <Select 
                    value={formData.image_url || ''} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, image_url: v }))}
                  >
                    <SelectTrigger data-testid="image-select">
                      <SelectValue placeholder="Selecciona una imagen" />
                    </SelectTrigger>
                    <SelectContent>
                      {mediaFiles.filter(m => m.type === 'image').map(m => (
                        <SelectItem key={m.id} value={m.url}>{m.filename}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Direct upload button */}
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleDirectImageUpload}
                      data-testid="direct-image-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      disabled={uploading}
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="upload-bg-btn"
                    >
                      <ImagePlus className="w-4 h-4 mr-2" />
                      {uploading ? 'Subiendo...' : 'Subir foto de certificado'}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Sube directamente o selecciona desde Biblioteca de Medios
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Imagen de Firma (opcional)</Label>
                  <Select 
                    value={formData.signature_url || 'none'} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, signature_url: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger data-testid="signature-select">
                      <SelectValue placeholder="Sin firma" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin firma</SelectItem>
                      {mediaFiles.filter(m => m.type === 'image').map(m => (
                        <SelectItem key={m.id} value={m.url}>{m.filename}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Size Settings */}
            <Card>
              <CardContent className="p-4">
                <Tabs defaultValue="text" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="text">Texto</TabsTrigger>
                    <TabsTrigger value="elements">Elementos</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="text" className="space-y-4 mt-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm" style={{ color: ELEMENT_TYPES.STUDENT_NAME.color }}>
                          Nombre Estudiante
                        </Label>
                        <Input
                          type="number"
                          className="w-20 h-8"
                          value={formData.student_name_size}
                          onChange={(e) => setFormData(prev => ({ ...prev, student_name_size: parseInt(e.target.value) || 48 }))}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-sm" style={{ color: ELEMENT_TYPES.COURSE_NAME.color }}>
                          Nombre Curso
                        </Label>
                        <Input
                          type="number"
                          className="w-20 h-8"
                          value={formData.course_name_size}
                          onChange={(e) => setFormData(prev => ({ ...prev, course_name_size: parseInt(e.target.value) || 36 }))}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-sm" style={{ color: ELEMENT_TYPES.DATE.color }}>
                          Fecha
                        </Label>
                        <Input
                          type="number"
                          className="w-20 h-8"
                          value={formData.date_size}
                          onChange={(e) => setFormData(prev => ({ ...prev, date_size: parseInt(e.target.value) || 24 }))}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-sm" style={{ color: ELEMENT_TYPES.CODE.color }}>
                          Código
                        </Label>
                        <Input
                          type="number"
                          className="w-20 h-8"
                          value={formData.code_size}
                          onChange={(e) => setFormData(prev => ({ ...prev, code_size: parseInt(e.target.value) || 20 }))}
                        />
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="elements" className="space-y-4 mt-4">
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm mb-2 block" style={{ color: ELEMENT_TYPES.SIGNATURE.color }}>
                          Firma (ancho x alto)
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            className="w-20 h-8"
                            value={formData.signature_width}
                            onChange={(e) => setFormData(prev => ({ ...prev, signature_width: parseInt(e.target.value) || 200 }))}
                          />
                          <span className="self-center">x</span>
                          <Input
                            type="number"
                            className="w-20 h-8"
                            value={formData.signature_height}
                            onChange={(e) => setFormData(prev => ({ ...prev, signature_height: parseInt(e.target.value) || 100 }))}
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm mb-2 block" style={{ color: ELEMENT_TYPES.QR_CODE.color }}>
                          Tamaño QR
                        </Label>
                        <Input
                          type="number"
                          className="w-20 h-8"
                          value={formData.qr_size}
                          onChange={(e) => setFormData(prev => ({ ...prev, qr_size: parseInt(e.target.value) || 150 }))}
                        />
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* Coordinates Display */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3 text-sm">Coordenadas Actuales</h3>
                <div className="text-xs space-y-1 font-mono bg-muted p-2 rounded">
                  <p style={{ color: ELEMENT_TYPES.STUDENT_NAME.color }}>Nombre: ({formData.student_name_x}, {formData.student_name_y})</p>
                  <p style={{ color: ELEMENT_TYPES.COURSE_NAME.color }}>Curso: ({formData.course_name_x}, {formData.course_name_y})</p>
                  <p style={{ color: ELEMENT_TYPES.DATE.color }}>Fecha: ({formData.date_x}, {formData.date_y})</p>
                  <p style={{ color: ELEMENT_TYPES.CODE.color }}>Código: ({formData.code_x}, {formData.code_y})</p>
                  <p style={{ color: ELEMENT_TYPES.SIGNATURE.color }}>Firma: ({formData.signature_x}, {formData.signature_y})</p>
                  <p style={{ color: ELEMENT_TYPES.QR_CODE.color }}>QR: ({formData.qr_x}, {formData.qr_y})</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Visual Editor */}
          <div className="lg:col-span-2">
            <Card className="h-full">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Vista Previa — Arrastra los elementos para posicionarlos
                  </h3>
                  {templateImage && (
                    <span className="text-xs text-muted-foreground">
                      {templateImage.width} x {templateImage.height}px (escala: {Math.round(imageScale * 100)}%)
                    </span>
                  )}
                </div>

                {/* QR tip banner */}
                {imageLoaded && (
                  <div className="mb-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600 flex items-start gap-2">
                    <QrCode className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" />
                    <span>
                      <strong>QR de verificación:</strong> Arrastra el elemento <span className="font-mono bg-slate-100 px-1 rounded">Código QR</span> para posicionarlo en el certificado.
                      El texto de verificación se dibujará automáticamente en <strong>2 líneas</strong> justo debajo del QR:
                      <br/><span className="font-mono">Verificar: <br/>IAASP-XXXXXXXX</span>
                      &nbsp;— el tamaño de fuente se ajusta solo para no salirse del borde.
                    </span>
                  </div>
                )}

                {!formData.image_url ? (
                  <div className="border-2 border-dashed border-muted rounded-lg h-96 flex flex-col items-center justify-center text-muted-foreground">
                    <Image className="w-12 h-12 mb-4" />
                    <p>Selecciona una imagen de fondo para comenzar</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => navigate('/admin/media')}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Ir a Biblioteca de Medios
                    </Button>
                  </div>
                ) : !imageLoaded ? (
                  <div className="border-2 border-dashed border-muted rounded-lg h-96 flex items-center justify-center">
                    <p>Cargando imagen...</p>
                  </div>
                ) : (
                  <div 
                    ref={containerRef}
                    className="relative border rounded-lg overflow-hidden bg-gray-100"
                    style={{
                      width: templateImage.width * imageScale,
                      height: templateImage.height * imageScale,
                      cursor: dragging ? 'grabbing' : 'default'
                    }}
                    data-testid="visual-editor-canvas"
                  >
                    {/* Template Background */}
                    <img
                      src={formData.image_url.startsWith('http') 
                        ? formData.image_url 
                        : `${process.env.REACT_APP_BACKEND_URL}${formData.image_url}`}
                      alt="Template"
                      style={{
                        width: templateImage.width * imageScale,
                        height: templateImage.height * imageScale,
                      }}
                      className="pointer-events-none"
                    />

                    {/* Draggable Elements */}
                    {Object.values(ELEMENT_TYPES).map(renderDraggableElement)}
                  </div>
                )}

                {/* Legend */}
                {imageLoaded && (
                  <div className="mt-4 flex flex-wrap gap-3">
                    {Object.values(ELEMENT_TYPES).map(el => (
                      <div key={el.id} className="flex items-center gap-1 text-xs">
                        <div 
                          className="w-3 h-3 rounded" 
                          style={{ backgroundColor: el.color }}
                        />
                        <span>{el.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateTemplateEditor;
